import { useEffect, useState } from 'react';
import { gql } from '../api';

// IN3: plattformsadmin kan dölja integrationer (Admin → Integrationer). Servern avgör vad som visas
// (integrationVisibility; API:t spärrar också nya kopplingar). Admin ser de dolda, märkta "Dold för användare".

export type IntegrationKey = 'telegram' | 'whatsapp' | 'sms' | 'garmin' | 'paj' | 'abeeway' | 'traccar';
export interface IntegrationVisibilityItem { key: IntegrationKey; label: string; hidden: boolean; visible: boolean }
export interface IntegrationVisibility {
  viewerIsAdmin: boolean;
  integrations: IntegrationVisibilityItem[];
  updatedBy: string | null;
  updatedAt: string | null;
}

/** Samma standard som servern (lib/hiddenIntegrations.ts): gäller tills svaret kommit, så inget blinkar fram. */
export const DEFAULT_HIDDEN: IntegrationKey[] = ['whatsapp', 'sms', 'abeeway'];

const FIELDS = 'viewerIsAdmin updatedBy updatedAt integrations{key label hidden visible}';

/** Visas integrationen för den här tittaren? */
export function integrationShown(v: IntegrationVisibility | null, key: IntegrationKey): boolean {
  if (!v) return !DEFAULT_HIDDEN.includes(key);
  return v.integrations.find(i => i.key === key)?.visible ?? true;
}

/** Admin ser en dold integration: visa märkningen "Dold för användare". */
export function integrationHiddenForUsers(v: IntegrationVisibility | null, key: IntegrationKey): boolean {
  return !!v?.viewerIsAdmin && !!v.integrations.find(i => i.key === key)?.hidden;
}

let current: IntegrationVisibility | null = null;
let pending: Promise<IntegrationVisibility | null> | null = null;
const listeners = new Set<(v: IntegrationVisibility | null) => void>();
const publish = (v: IntegrationVisibility | null) => { current = v; listeners.forEach(l => l(v)); };

export function fetchIntegrationVisibility(force = false): Promise<IntegrationVisibility | null> {
  if (!force && current) return Promise.resolve(current);
  if (!force && pending) return pending;
  pending = gql<{ integrationVisibility: IntegrationVisibility }>(`query{integrationVisibility{${FIELDS}}}`)
    .then(d => { publish(d.integrationVisibility); return d.integrationVisibility; })
    .catch(() => current)
    .finally(() => { pending = null; });
  return pending;
}

export async function setIntegrationHidden(key: IntegrationKey, hidden: boolean): Promise<IntegrationVisibility> {
  const d = await gql<{ setIntegrationHidden: IntegrationVisibility }>(
    `mutation($key:ID!,$hidden:Boolean!){setIntegrationHidden(key:$key,hidden:$hidden){${FIELDS}}}`, { key, hidden });
  publish(d.setIntegrationHidden);
  return d.setIntegrationHidden;
}

/** Synligheten för den inloggade. Hämtas en gång per sidladdning och uppdateras när admin ändrar. */
export function useIntegrationVisibility() {
  const [v, setV] = useState<IntegrationVisibility | null>(current);
  useEffect(() => {
    listeners.add(setV);
    void fetchIntegrationVisibility();
    return () => { listeners.delete(setV); };
  }, []);
  return {
    visibility: v,
    shown: (key: IntegrationKey) => integrationShown(v, key),
    hiddenForUsers: (key: IntegrationKey) => integrationHiddenForUsers(v, key),
  };
}
